import { useState, useEffect, useCallback } from "react";
import { Plus, X } from "lucide-react";
import { cn } from "../../lib/utils";
import FormField from "../ui/FormField";
import { Button } from "../ui/Button";
import { SunbedsTable, SunbedRoom } from "../../components/sunbeds/SunbedsTable";

import {
  createSunbedRoom,
  getSunbedRooms,
  updateSunbedRoom,
  deleteSunbedRoom
} from "../../services/sunbeds";
export interface PricingOption {
  id: string;
  label: string;
  type: "per_minute" | "fixed";
  price: number;
  duration: number;
}

export interface PackageFormInput {
  id?: string;
  label: string;
  status: "active" | "inactive";
  // Can be a list of room ids (string) or detailed room objects from API
  sunbed_room: (string | SunbedRoom)[];
  pricing: PricingOption[];
}

interface AddPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PackageFormInput) => void;
  existingPackage?: PackageFormInput | null;
  mode: "add" | "edit";
}

export const AddPackageModal = ({
  isOpen,
  onClose,
  onSave,
  existingPackage,
  mode,
}: AddPackageModalProps) => {
  const [rooms, setRooms] = useState<SunbedRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  // We track selected rooms by their label so that we can match
  // against the package rooms coming from the sunbeds list, which
  // currently only exposes room labels (no stable ids).
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [pricing, setPricing] = useState<PricingOption[]>([]);

  // Fetch Rooms from API
  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSunbedRooms();
      if (response && response.data) {
        // Map API response to UI model
        const mappedRooms: SunbedRoom[] = response.data.map((item: any) => ({
          id: item.id.toString(),
          label: item.label_formatted || item.label,
          status: (item.availability_formatted || item.availability) as "Available" | "In-session" | "Maintenance" | "Cleaning",
          totalDuration: item.total_hours_formatted || "0 mins"
        }));
        setRooms(mappedRooms);
      }
    } catch (error) {
      console.error("Failed to fetch sunbed rooms", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (existingPackage && mode === "edit") {
        setLabel(existingPackage.label);
        setStatus(existingPackage.status);
        // Normalise existing package rooms into a list of labels for selection
        setSelectedRooms(
          (existingPackage.sunbed_room || []).map((room) =>
            typeof room === "string" ? room : room.label
          )
        );
        setPricing(existingPackage.pricing);
      } else {
        setLabel("");
        setStatus("active");
        setSelectedRooms([]);
        setPricing([]);
      }
      fetchRooms();
    }
  }, [isOpen, existingPackage, mode, fetchRooms]);

  const handleAddPricing = () => {
    setPricing([
      ...pricing,
      {
        id: Date.now().toString(),
        label: "",
        type: "per_minute",
        price: 0,
        duration: 0,
      },
    ]);
  };

  const handleUpdatePricing = (
    id: string,
    field: keyof PricingOption,
    value: any
  ) => {
    setPricing(
      pricing.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const handleRemovePricing = (id: string) => {
    setPricing(pricing.filter((p) => p.id !== id));
  };

  const toggleRoom = (roomLabel: string) => {
    setSelectedRooms((prev) =>
      prev.includes(roomLabel)
        ? prev.filter((r) => r !== roomLabel)
        : [...prev, roomLabel]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const ConditionalPricing = pricing.map((p) => {
      const { duration, ...rest } = p;
      if (p.type === "fixed") {
        return p;
      }
      return rest as PricingOption;
    });

    // while the UI can still work with human-friendly labels.
    const selectedRoomIds = selectedRooms
      .map((label) => rooms.find((r) => r.label === label)?.id)
      .filter((id): id is string => Boolean(id));

    onSave({
      label,
      status,
      sunbed_room: selectedRoomIds,
      pricing: ConditionalPricing,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#885ABB] flex justify-between items-center bg-[#885ABB] rounded-t-xl">
          <h2 className="text-xl font-bold text-white">
            {mode === "add" ? "Add Package" : "Edit Package"}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
        >
          <div className="grid grid-cols-2 gap-6">
            <FormField
              label="Label"
              name="label"
              value={label}
              onChange={(_, val) => setLabel(val)}
              placeholder="Package Name"
            />
            <FormField
              label="Status"
              name="status"
              type="select"
              value={status}
              onChange={(_, val) => setStatus(val as "active" | "inactive")}
              options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]}
              placeholder="Select status"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#402B69]">Sunbeds (Rooms)</label>
            <div className="min-h-[50px] rounded-lg border border-gray-200 bg-[#F9F9F9] px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    onClick={() => toggleRoom(room.label)}
                    className={cn(
                      "px-3 py-1 text-sm rounded-full transition-colors border",
                      selectedRooms.includes(room.label)
                        ? "bg-[#F3E8FF] border-[#C4A6E1] text-[#885ABB] font-medium"
                        : "bg-white border-gray-200 text-gray-600 hover:border-[#C4A6E1]"
                    )}
                  >
                    {room.label}
                  </button>
                ))}
              </div>
              {selectedRooms.length === 0 && (
                <p className="text-sm text-gray-400 mt-1">Select rooms to apply to this package</p>
              )}
            </div>
          </div>

          {/* Pricing Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <h3 className="font-semibold text-[#402B69]">Pricing</h3>
              <Button
                type="button"
                onClick={handleAddPricing}
                variant="outline"
                className="text-[#885ABB] border-[#885ABB] hover:bg-[#F3E8FF]"
              >
                + Add Pricing 
              </Button>
            </div>

            <div className="space-y-3">
              {pricing.map((p) => (
                <div
                  key={p.id}
                  className="flex gap-4 items-end bg-gray-50 p-3 rounded-lg border border-gray-100"
                >
                  <div className="flex-1">
                    <FormField
                      label="Price Label "
                      name={`price-label-${p.id}`}
                      value={p.label}
                      onChange={(_, val) => handleUpdatePricing(p.id, "label", val)}
                      placeholder="e.g. Standard"
                    />
                  </div>
                  <div className="w-[130px]">
                    <FormField
                      label="Type"
                      name={`price-type-${p.id}`}
                      type="select"
                      value={p.type}
                      onChange={(_, val) => handleUpdatePricing(p.id, "type", val)}
                      options={[
                        { value: "per_minute", label: "Per minute" },
                        { value: "fixed", label: "Fixed" },
                      ]}
                      placeholder="Select Type"
                    />
                  </div>
                  {p.type === "fixed" && (
                    <div className="w-[100px]">
                      <FormField
                        label="Duration"
                        name={`price-duration-${p.id}`}
                        value={p.duration?.toString() || "0"}
                        onChange={(_, val) =>
                          handleUpdatePricing(p.id, "duration", parseFloat(val) || 0)
                        }
                        placeholder="Mins"
                      />
                    </div>
                  )}
                  <div className="w-[100px]">
                    <FormField
                      label="Price"
                      name={`price-val-${p.id}`}
                      value={p.price.toString()}
                      onChange={(_, val) =>
                        handleUpdatePricing(p.id, "price", parseFloat(val) || 0)
                      }
                      placeholder="0.00"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleRemovePricing(p.id)}
                    className="mb-[2px] text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {pricing.length === 0 && (
                <p className="text-sm text-gray-400 italic">No pricing added yet.</p>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            type="button"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-[#885ABB] hover:bg-[#6f47a2] text-white">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
